import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageOff } from "lucide-react";
import api, { imgUrl, inr, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TableToolbar, matches, exportRows, Empty, STATUS_STYLE } from "./shared";

const COLUMNS = [
  { label: "Date", value: (i) => fmtDate(i.created_at) },
  { label: "Investor", value: "user_name" },
  { label: "Email", value: "user_email" },
  { label: "Plan", value: "plan_title" },
  { label: "Category", value: "category" },
  { label: "Amount (₹)", value: "amount" },
  { label: "Payment Ref", value: "payment_reference" },
  { label: "Screenshot", value: (i) => (i.payment_proof ? "Yes" : "No") },
  { label: "Status", value: "status" },
  { label: "Admin Note", value: "admin_note" },
  { label: "Reviewed", value: (i) => fmtDate(i.updated_at) },
  { label: "Maturity Date", value: (i) => (i.maturity_date ? i.maturity_date.slice(0, 10) : "") },
  { label: "Maturity Amount (₹)", value: "maturity_amount" },
  { label: "Payout Amount (₹)", value: (i) => i.payout?.amount ?? "" },
  { label: "Payout Date", value: (i) => i.payout?.date ?? "" },
  { label: "Payout Ref", value: (i) => i.payout?.reference ?? "" },
];
const FILTERS = ["all", "pending", "active", "completed", "rejected"];

const ReviewDialog = ({ inv, onClose, onDone }) => {
  const [note, setNote] = useState(inv?.admin_note || "");
  const [busy, setBusy] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payout, setPayout] = useState({
    amount: inv?.payout?.amount ?? inv?.maturity_amount ?? inv?.amount ?? "",
    date: inv?.payout?.date || new Date().toISOString().slice(0, 10),
    reference: inv?.payout?.reference || "",
  });
  const [investor, setInvestor] = useState(null);
  useEffect(() => {
    if (!inv) return;
    api.get("/admin/users").then((r) => setInvestor(r.data.find((u) => u.id === inv.user_id) || null)).catch(() => {});
  }, [inv]);
  if (!inv) return null;
  const act = async (status) => {
    if (status === "completed" && !payoutOpen) {
      setPayoutOpen(true);
      return;
    }
    setBusy(true);
    try {
      const body = { status, admin_note: note };
      if (status === "completed") Object.assign(body, { payout_amount: Number(payout.amount), payout_date: payout.date, payout_reference: payout.reference });
      await api.patch(`/admin/investments/${inv.id}`, body);
      toast.success(status === "active" ? "Investment approved — investor notified by email" : status === "completed" ? "Payout recorded — investor notified by email" : `Marked ${status} — investor notified by email`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const bank = investor?.bank || {};
  const kycStatus = investor?.kyc?.status || "none";
  const waMsg = (kind) => {
    const text = kind === "approved"
      ? `Hi ${inv.user_name}, your investment of ${inr(inv.amount)} in "${inv.plan_title}" with MetrixWorks has been APPROVED and is now active. Maturity: ${inv.maturity_date ? fmtDate(inv.maturity_date).split(",")[0] : "as per plan"}. Thank you for investing with us!`
      : `Hi ${inv.user_name}, your payout of ${inr(inv.payout?.amount ?? inv.maturity_amount)} for "${inv.plan_title}" has been transferred${inv.payout?.reference ? ` (Ref ${inv.payout.reference})` : ""}. Thank you for investing with MetrixWorks!`;
    const phone = (investor?.phone || "").replace(/\D/g, "");
    const to = phone ? (phone.length === 10 ? `91${phone}` : phone) : "";
    return `https://wa.me/${to}?text=${encodeURIComponent(text)}`;
  };
  const hasBank = Boolean(bank.account_number || bank.upi_id);
  const field = "bg-transparent border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30 gold-focus w-full";
  const btn = "px-5 py-3 font-accent text-[10px] tracking-[0.2em] uppercase border transition-colors disabled:opacity-50";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border-[#D4AF37]/30 text-white p-0 rounded-none" data-testid="review-dialog">
        <div className="grid md:grid-cols-2">
          <div className="bg-black flex items-center justify-center min-h-[320px]">
            {inv.payment_proof ? (
              <img src={imgUrl(inv.payment_proof)} alt="Payment screenshot" className="max-h-[85vh] w-full object-contain" data-testid="review-proof-image" />
            ) : (
              <div className="text-center text-white/30 p-10" data-testid="review-proof-missing">
                <ImageOff className="mx-auto mb-3" />
                <p className="text-xs font-accent tracking-[0.2em] uppercase">No payment screenshot uploaded</p>
              </div>
            )}
          </div>
          <div className="p-8">
            <DialogHeader className="text-left">
              <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Review payment</p>
              <DialogTitle className="font-display text-2xl tracking-tight text-white">{inr(inv.amount)} · {inv.plan_title}</DialogTitle>
              <DialogDescription className="text-white/50 text-sm">{inv.user_name} · {inv.user_email}</DialogDescription>
            </DialogHeader>
            <dl className="mt-6 grid grid-cols-2 gap-y-3 text-sm border-t border-white/10 pt-6">
              <dt className="text-white/40">Payment reference</dt><dd data-testid="review-payment-ref">{inv.payment_reference || "—"}</dd>
              <dt className="text-white/40">Requested</dt><dd>{fmtDate(inv.created_at)}</dd>
              <dt className="text-white/40">Current status</dt><dd className="capitalize">{inv.status}</dd>
              {inv.reviewed_by && (<><dt className="text-white/40">Last reviewed by</dt><dd>{inv.reviewed_by}</dd></>)}
              {inv.maturity_date && (<><dt className="text-white/40">Matures</dt><dd data-testid="review-maturity">{fmtDate(inv.maturity_date)} · {inr(inv.maturity_amount)}</dd></>)}
              {inv.payout && (<><dt className="text-white/40">Payout recorded</dt><dd className="text-[#D4AF37]" data-testid="review-payout-recorded">{inr(inv.payout.amount)} on {inv.payout.date}{inv.payout.reference ? ` · ${inv.payout.reference}` : ""}</dd></>)}
            </dl>
            <div className="mt-4 border border-white/10 p-4 text-xs" data-testid="review-investor-bank">
              <div className="flex items-center justify-between mb-2">
                <p className="font-accent text-[9px] tracking-[0.25em] uppercase text-white/40">Investor payout account</p>
                <span data-testid="review-kyc-status" className={`font-accent text-[9px] tracking-[0.15em] uppercase border px-2 py-0.5 ${kycStatus === "verified" ? "text-emerald-400 border-emerald-400/40" : kycStatus === "pending" ? "text-[#D4AF37] border-[#D4AF37]/40" : "text-yellow-400 border-yellow-400/40"}`}>
                  KYC {kycStatus === "none" ? "missing" : kycStatus}
                </span>
              </div>
              {investor === null ? (
                <p className="text-white/40">Loading investor details…</p>
              ) : hasBank ? (
                <div className="grid grid-cols-2 gap-y-1 text-white/70">
                  <span>{bank.account_name || "—"}</span><span>{bank.bank_name || "—"}</span>
                  <span className="font-mono">{bank.account_number || "—"}</span><span className="font-mono">{bank.ifsc || "—"}</span>
                  {bank.upi_id && <span className="col-span-2 font-mono">UPI · {bank.upi_id}</span>}
                </div>
              ) : (
                <p className="text-yellow-400">Investor has not added payout bank details yet.</p>
              )}
            </div>
            <textarea
              data-testid="review-note-input"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note for your records (optional)"
              className="mt-6 w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus"
            />
            {payoutOpen && (
              <div className="mt-6 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 space-y-3" data-testid="payout-form">
                <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Record payout</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-[10px] text-white/40 space-y-1"><span>Amount paid (₹)</span>
                    <input data-testid="payout-amount-input" type="number" min="1" step="0.01" value={payout.amount} onChange={(e) => setPayout({ ...payout, amount: e.target.value })} className={field} />
                  </label>
                  <label className="text-[10px] text-white/40 space-y-1"><span>Payout date</span>
                    <input data-testid="payout-date-input" type="date" value={payout.date} onChange={(e) => setPayout({ ...payout, date: e.target.value })} className={`${field} [color-scheme:dark]`} />
                  </label>
                </div>
                <input data-testid="payout-reference-input" placeholder="UTR / transaction reference (optional)" value={payout.reference} onChange={(e) => setPayout({ ...payout, reference: e.target.value })} className={field} />
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <button data-testid="review-approve-button" disabled={busy} onClick={() => act("active")} className={`${btn} border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-black`}>Approve</button>
              <button data-testid="review-reject-button" disabled={busy} onClick={() => act("rejected")} className={`${btn} border-red-400 text-red-400 hover:bg-red-400 hover:text-black`}>Reject</button>
              <button data-testid="review-complete-button" disabled={busy} onClick={() => act("completed")} className={`${btn} border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black`}>{payoutOpen ? "Confirm Payout & Complete" : "Mark Completed"}</button>
              <button data-testid="review-pending-button" disabled={busy} onClick={() => act("pending")} className={`${btn} border-white/20 text-white/60 hover:border-white/50`}>Back to Pending</button>
            </div>
            {(inv.status === "active" || inv.status === "completed") && (
              <div className="mt-5 border-t border-white/10 pt-4" data-testid="notify-section">
                <p className="font-accent text-[9px] tracking-[0.25em] uppercase text-white/40 mb-2">Notify investor · email already sent automatically</p>
                {investor?.phone ? (
                  <a href={waMsg(inv.status === "active" ? "approved" : "paid")} target="_blank" rel="noopener noreferrer" data-testid="notify-whatsapp-link" className="inline-flex items-center gap-2 border border-[#25D366]/60 text-[#25D366] px-4 py-2 font-accent text-[10px] tracking-[0.2em] uppercase hover:bg-[#25D366] hover:text-black transition-colors">
                    Send WhatsApp · {inv.status === "active" ? "approval" : "payout"} note
                  </a>
                ) : (
                  <p className="text-xs text-white/40" data-testid="notify-no-phone">Investor has no phone number on file for WhatsApp.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const InvestmentsTab = ({ initialFilter = "all", onChanged }) => {
  const [items, setItems] = useState([]);
  const [matured, setMatured] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [review, setReview] = useState(null);

  const load = () => {
    api.get("/admin/investments").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/matured").then((r) => setMatured(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setFilter(initialFilter); }, [initialFilter]);

  const rows = items.filter((i) => (filter === "all" || i.status === filter) && matches(i, q));

  return (
    <div data-testid="admin-investments-tab">
      {matured.length > 0 && (
        <div className="mb-8 border border-[#D4AF37]/50 bg-[#D4AF37]/5 p-5" data-testid="matured-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">
              Payouts due today · {matured.length} · {inr(matured.reduce((s, i) => s + i.maturity_amount, 0))}
            </p>
            <p className="text-xs text-white/40">A digest email is sent to you each morning while these remain open.</p>
          </div>
          <ul className="mt-3 divide-y divide-white/5">
            {matured.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm" data-testid={`matured-row-${i.id}`}>
                <span className="text-white">{i.user_name} <span className="text-white/40">· {i.plan_title}</span></span>
                <span className="text-white/60 text-xs">Matured {fmtDate(i.maturity_date).split(",")[0]}</span>
                <span className="text-[#D4AF37]">{inr(i.maturity_amount)}</span>
                <button data-testid={`matured-pay-${i.id}`} onClick={() => setReview(i)} className="border border-[#D4AF37] text-[#D4AF37] px-3 py-1.5 font-accent text-[9px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors">Record Payout</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <TableToolbar title="Investments" count={rows.length} query={q} onQuery={setQ} placeholder="Search investor, plan, ref…" testId="investments" onExport={() => exportRows("investments", COLUMNS, rows)}>
        <div className="flex border border-white/10" data-testid="investments-status-filter">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`investments-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 font-accent text-[9px] tracking-[0.2em] uppercase transition-colors ${filter === f ? "bg-[#D4AF37] text-black" : "text-white/50 hover:text-white"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </TableToolbar>
      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-accent text-[10px] tracking-[0.2em] uppercase text-white/35">
          <span className="col-span-3">Investor</span>
          <span className="col-span-3">Plan</span>
          <span className="col-span-2">Amount</span>
          <span className="col-span-1">Proof</span>
          <span className="col-span-1">Status</span>
          <span className="col-span-2 text-right">Action</span>
        </div>
        {rows.map((inv) => (
          <div key={inv.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 last:border-0 items-center" data-testid={`admin-investment-row-${inv.id}`}>
            <div className="col-span-3 min-w-0">
              <p className="text-sm text-white truncate">{inv.user_name}</p>
              <p className="text-xs text-white/35 mt-1 truncate">{inv.user_email}</p>
            </div>
            <div className="col-span-3 min-w-0">
              <p className="text-sm text-white/70 truncate">{inv.plan_title}</p>
              <p className="text-xs text-white/35 mt-1">{fmtDate(inv.created_at)}{inv.payment_reference ? ` · Ref ${inv.payment_reference}` : ""}</p>
            </div>
            <span className="col-span-2 text-sm text-white">{inr(inv.amount)}</span>
            <div className="col-span-1">
              {inv.payment_proof ? (
                <img src={imgUrl(inv.payment_proof)} alt="Proof" className="w-9 h-12 object-cover border border-white/10 cursor-pointer" onClick={() => setReview(inv)} data-testid={`proof-thumb-${inv.id}`} />
              ) : (
                <span className="text-[10px] text-white/30" data-testid={`proof-missing-${inv.id}`}>None</span>
              )}
            </div>
            <span className={`col-span-1 font-accent text-[9px] tracking-[0.15em] uppercase border px-2 py-1 text-center ${STATUS_STYLE[inv.status] || ""}`} data-testid={`admin-investment-status-${inv.id}`}>{inv.status}</span>
            <div className="col-span-2 flex justify-end">
              <button
                data-testid={`review-button-${inv.id}`}
                onClick={() => setReview(inv)}
                className={`px-4 py-2 font-accent text-[9px] tracking-[0.2em] uppercase border transition-colors ${inv.status === "pending" ? "border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black" : "border-white/20 text-white/60 hover:border-white/50"}`}
              >
                {inv.status === "pending" ? "Review & Approve" : "Manage"}
              </button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty testId="investments-empty" text={q || filter !== "all" ? "No investments match." : "No investments yet."} />}
      </div>
      <ReviewDialog key={review?.id} inv={review} onClose={() => setReview(null)} onDone={() => { setReview(null); load(); onChanged?.(); }} />
    </div>
  );
};
